import React, { useMemo } from 'react';
import { Pressable, View } from 'react-native';
import {
  barFraction,
  cartTotals,
  countsFromOrders,
  fmt,
  maxItemOrders,
  rankItems,
  useAppStore,
} from '@restorant/core';
import { radius, useColors } from '../theme';
import { useItemsLabel, useT, useTableLabel } from '../i18n';
import { useCategories, useItemName, useMenuItems } from '../menu';
import { Icon } from '../icons/Icon';
import { Screen } from '../components/Screen';
import { BarRow } from '../components/BarRow';
import { EmptyNote } from '../components/EmptyNote';
import { OrderRow } from '../components/OrderRow';
import { Panel } from '../components/Panel';
import { SectionTitle } from '../components/SectionTitle';
import { StatTile } from '../components/StatTile';
import { T } from '../components/Text';

function RunningTableRow({ tableId }: { tableId: string }) {
  const colors = useColors();
  const t = useT();
  const tableLabel = useTableLabel();
  const items = useItemsLabel();
  const table = useAppStore((s) => s.tables[tableId]);
  const openTable = useAppStore((s) => s.openTable);
  const setView = useAppStore((s) => s.setView);
  if (!table) return null;

  const { grand, qty } = cartTotals(table.cart, table.discount);
  const who = table.customer.name.trim();

  return (
    <Pressable
      onPress={() => {
        setView('order');
        openTable(tableId);
      }}
      style={({ hovered }: any) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 11,
        paddingHorizontal: 14,
        backgroundColor: colors.surface2,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: hovered ? colors.goldDim : colors.line,
        marginBottom: 8,
      })}
    >
      <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: colors.good }} />
      <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
        <T size={13.5} weight={700} numberOfLines={1}>
          {tableLabel(tableId)}
          {who ? ` · ${who}` : ''}
        </T>
        <T size={11.5} color={colors.textFaint}>
          {items(qty)} {t('items_in_order')}
        </T>
      </View>
      <T size={14} weight={700} color={colors.gold}>
        {fmt(grand)}
      </T>
      <Icon name="chevronRight" size={16} color={colors.textFaint} />
    </Pressable>
  );
}

export function DashboardScreen({ contentPadding }: { contentPadding: number }) {
  const colors = useColors();
  const t = useT();
  const offers = useAppStore((s) => s.offers);
  const orders = useAppStore((s) => s.orders);
  const orderCounts = useMemo(() => countsFromOrders(orders), [orders]);
  const categories = useCategories();
  const menuItems = useMenuItems();
  const itemName = useItemName();
  const tables = useAppStore((s) => s.tables);
  const openOrderDetail = useAppStore((s) => s.openOrderDetail);
  const setView = useAppStore((s) => s.setView);

  const activeOffers = Object.values(offers).filter((p) => p > 0).length;
  const revenue = orders.reduce((s, o) => s + o.total, 0);
  const runningIds = Object.keys(tables).filter((id) => tables[id].cart.length > 0);

  const top = rankItems(menuItems, orderCounts).slice(0, 5);
  const max = maxItemOrders(menuItems, orderCounts);
  const hasActivity = top.some((r) => r.orders > 0);

  return (
    <Screen contentPadding={contentPadding}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
        <StatTile value={String(menuItems.length)} label={t('stat_items')} icon="menu" />
        <StatTile value={String(categories.length)} label={t('stat_categories')} icon="grid" />
        <StatTile value={String(activeOffers)} label={t('stat_on_offer')} icon="offers" />
        <StatTile value={String(runningIds.length)} label={t('stat_open_tables')} icon="table" />
        <StatTile value={String(orders.length)} label={t('stat_orders')} icon="order" />
        <StatTile value={fmt(revenue)} label={t('stat_revenue')} icon="rupee" />
      </View>

      <SectionTitle title={t('running_tables')} icon="table" />
      {runningIds.length ? (
        <View>
          {runningIds.map((id) => (
            <RunningTableRow key={id} tableId={id} />
          ))}
        </View>
      ) : (
        <Panel>
          <EmptyNote>{t('no_running_tables')}</EmptyNote>
        </Panel>
      )}

      <SectionTitle title={t('top_sellers')} icon="flame" hint={t('top_sellers_hint')} />
      <Panel>
        {hasActivity ? (
          top.map((r) => (
            <BarRow
              key={r.item.id}
              name={itemName(r.item)}
              fraction={barFraction(r.orders, max)}
              count={r.orders}
            />
          ))
        ) : (
          <EmptyNote>{t('no_sales_yet')}</EmptyNote>
        )}
      </Panel>

      <SectionTitle
        title={t('recent_orders')}
        icon="clock"
        hint={orders.length ? t('nav_orders') : undefined}
        hintIcon={orders.length ? 'chevronRight' : undefined}
        hintColor={colors.gold}
        onHintPress={orders.length ? () => setView('orders') : undefined}
      />
      {orders.length ? (
        <View>
          {[...orders]
            .reverse()
            .slice(0, 5)
            .map((o) => (
              <OrderRow key={o.id} order={o} onPress={() => openOrderDetail(o.id)} />
            ))}
        </View>
      ) : (
        <Panel>
          <EmptyNote>{t('no_orders_yet')}</EmptyNote>
        </Panel>
      )}
    </Screen>
  );
}
